/**
 * AdminModeContext - Session-only admin debug mode for internal testing
 * 
 * Security: 
 * - State is session-only (no localStorage)
 * - Only accessible via keyboard chord (Ctrl+Shift+A)
 * - Requires admin role + allowlist
 * - Gated by VITE_INTERNAL_ADMIN build flag
 */

import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { toast } from 'sonner';

interface AdminModeContextType {
  enabled: boolean;
  toggle: () => void;
}

const AdminModeContext = createContext<AdminModeContextType | null>(null);

export function AdminModeProvider({ children }: { children: ReactNode }) {
  // Session-only state - resets on refresh
  const [enabled, setEnabled] = useState(false);

  const toggle = useCallback(() => {
    setEnabled(prev => {
      const next = !prev;
      toast.info(next ? 'Admin mode enabled' : 'Admin mode disabled', {
        duration: 2000,
      });
      return next;
    });
  }, []);

  return (
    <AdminModeContext.Provider value={{ enabled, toggle }}>
      {children}
    </AdminModeContext.Provider>
  );
}

export function useAdminMode() {
  const ctx = useContext(AdminModeContext);
  // Return safe defaults when outside provider (prevents errors)
  if (!ctx) {
    return { enabled: false, toggle: () => {} };
  }
  return ctx;
}
